/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 *
 * Component: SlideFilter
 * Date: 12/8/2020
 *
 */

import React, { useReducer, useRef, useCallback, useEffect } from 'react';
import { debounce } from 'lodash';

import DateUtils from 'utils/date';
import FilterLayout from 'components/FilterLayout';
import { numFormatting } from 'utils/commonFunctions';
import { Input, Button, Slider, Text } from 'components/common';

import styles from './style.module.css';

function SlideFilter({
  type,
  min,
  max,
  selectedValues,
  filter,
  onFilterApplied,
  moreFilter,
}: any): any {
  const slideFilterRef = useRef<any>();

  /**
   * InitialState Object.
   *  */
  const initialState = {
    minState: Math.floor(min) || 0,
    maxState: Math.ceil(max) ?? 100,
    minValue: 0,
    maxValue: 100,
    showFilter: false,
    sliderError: false,
    errorMessage: 'Min value cannot be less than 0',
    formatterValue: type === 'amount' ? '$ ' : '',
  };

  const [reducerState, dispatch] = useReducer(reducer, initialState);
  const {
    minState,
    maxState,
    minValue,
    maxValue,
    showFilter,
    sliderError,
    errorMessage,
    formatterValue,
  }: any = reducerState;

  /**
   * Fixes the range overflow and underflow for v.
   * @param {Numer} v: value of the Range to fix, out of Range issues.
   * @returns {Number}: After adjustiong v into the range.
   */
  const getOutsideRangeValue = (v: number) => {
    let value = v;
    const minS = minState || 0;
    const maxS = maxState || 100;
    if (type === 'duration') {
      if (value < minS) {
        value = minS;
      } else if (value > maxS) {
        value = maxS;
      }
    }
    return value;
  };

  /**
   * Checks for Overflow and UnderFlow issues, and represents back the message.
   * @param {String} durationType: Type of duration i.e, duration, etc.
   * @param {*} argMinValue : min value of args
   * @param {*} argMaxValue : max value of args.
   * @returns {Object}
   */
  const checkValueError = (
    durationType: string,
    argMinValue: any,
    argMaxValue: any,
  ): any => {
    let newSliderError = false;
    let newErrorMessage = '';
    if (type === 'duration') {
      if (argMinValue < 0 || argMaxValue < 0) {
        newSliderError = true;
        newErrorMessage = `${durationType} duration must be greater than 0`;
      } else if (argMaxValue < argMinValue) {
        newSliderError = true;
        newErrorMessage =
          'Maximum duration must be greater than minimum duration';
      }
    }

    return {
      newSliderError,
      newErrorMessage,
    };
  };

  function reducer(
    state: { maxValue: any; minValue: any },
    action: { type: any; payload: any },
  ) {
    switch (action.type) {
      case 'onMinValueChange': {
        const value = action.payload;
        let newMinValue = value ? parseFloat(value) : 0;
        let newMaxValue = state.maxValue;
        const { newSliderError, newErrorMessage } = checkValueError(
          'Minimum',
          newMinValue,
          newMaxValue,
        );

        if (
          type === 'duration' &&
          (newMinValue < minState || newMinValue > maxState)
        ) {
          newMinValue = getOutsideRangeValue(newMinValue);
          newMaxValue = getOutsideRangeValue(newMaxValue);
        }
        return {
          ...state,
          sliderError: newSliderError,
          errorMessage: newErrorMessage,
          minValue: newMinValue,
          maxValue: newMaxValue,
        };
      }
      case 'onMaxValueChange': {
        const value = action.payload;
        let newMaxValue = value ? parseFloat(value) : 0;
        let newMinValue = state.minValue;
        const { newSliderError, newErrorMessage } = checkValueError(
          'Maximum',
          newMinValue,
          newMaxValue,
        );
        if (newSliderError) {
          return { ...state, maxValue: newMaxValue };
        }
        if (
          type === 'duration' &&
          (newMaxValue < minState || newMaxValue > maxState)
        ) {
          newMinValue = getOutsideRangeValue(newMinValue);
          newMaxValue = getOutsideRangeValue(newMaxValue);
        }
        return {
          ...state,
          sliderError: newSliderError,
          errorMessage: newErrorMessage,
          minValue: newMinValue,
          maxValue: newMaxValue,
        };
      }
      case 'onSliderChange': {
        const values = action.payload;
        return {
          ...state,
          minValue: values[0],
          maxValue: values[1],
        };
      }
      case 'clear': {
        return {
          ...state,
          ...initialState,
        };
      }
      case 'showFilter':
        return {
          ...state,
          showFilter: action.payload,
        };
      default:
        throw new Error();
    }
  }

  /**
   * Debouncing in FilterApplied.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const delayedQuery = useCallback(
    // This is used to prevent the performance hit of slider filter
    debounce((field, e) => onFilterApplied(field, e), 100),
    [],
  );

  useEffect(() => {
    if (moreFilter) {
      delayedQuery(filter.field, [minValue, maxValue]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minValue, maxValue, moreFilter]);

  const processSelectedValueByType = (valueArg: string | number) => {
    let value;
    if (filter.subtype === 'duration') {
      value = DateUtils.formatDuration(valueArg as string);
    } else if (filter.subtype === 'amount') {
      value = `$${numFormatting(valueArg as number)}`;
    } else {
      value = numFormatting(valueArg as number);
    }
    return value;
  };

  /**
   * Function to get Button name as it is variable depending on selected values
   *
   * @return -> button name
   *
   */
  const getButtonName = () => {
    let filterName = filter.name;
    const selectedValue = selectedValues;
    if (selectedValue?.length) {
      if (min === selectedValue[0] && max === selectedValue[1]) {
        filterName = filter.name;
      } else if (min !== selectedValue[0] && max !== selectedValue[1]) {
        filterName = `${processSelectedValueByType(
          selectedValue[0],
        )} - ${processSelectedValueByType(selectedValue[1])}`;
      } else if (min === selectedValue[0]) {
        filterName = `Less than ${processSelectedValueByType(
          selectedValue[1],
        )}`;
      } else {
        filterName = `More than ${processSelectedValueByType(
          selectedValue[0],
        )}`;
      }
    } else {
      filterName = filter.name;
    }

    return filterName;
  };

  /**
   * returns JSX.
   * */
  const getFilter = () => (
    <div className={styles.sliderFilterContent}>
      <div className={styles.sliderContainer}>
        <Slider
          range
          min={Math.floor(min)}
          max={Math.ceil(max)}
          tooltipVisible={false}
          defaultValue={[minValue, maxValue]}
          value={[minValue, maxValue]}
          onChange={(e: any) => {
            dispatch({ type: 'onSliderChange', payload: e });
          }}
        />
      </div>
      <div className={styles.sliderInputContainer}>
        <Input
          type="number"
          min={Math.floor(min)}
          formatter={(value: any) => `${formatterValue}${value}`}
          parser={(value: string) => value.replace(/\$|:|,/gi, '')}
          className="more-slider-input-box"
          value={minValue}
          onChange={(e: any) => {
            dispatch({ type: 'onMinValueChange', payload: e });
          }}
        />
        <Input
          type="number"
          max={Math.floor(max)}
          formatter={(value: any) => `${formatterValue}${value}`}
          parser={(value: string) => value.replace(/\$|:|,/gi, '')}
          className="more-slider-input-box"
          value={maxValue}
          onChange={(e: any) => {
            dispatch({ type: 'onMaxValueChange', payload: e });
          }}
        />
      </div>
      {sliderError && <p className="error-message-span">{errorMessage}</p>}
    </div>
  );

  /**
   * @returns {JSX} based showFilter or moreFilter.
   */
  const getFilterComponent = () => {
    let component;
    if (showFilter || moreFilter) {
      if (moreFilter) {
        /**
         *as slide filter don't need layout buttons in morefilters
         */
        component = getFilter();
      } else {
        component = (
          <FilterLayout
            onCancel={() => handleClickOutside()}
            disabled={sliderError}
            onApply={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
              onFilterApplied(filter.field, [minValue, maxValue]);
              handleClickOutside();
            }}
            onClear={(e: { stopPropagation: () => void }) => {
              e.stopPropagation();
              onFilterApplied(filter.field, [Math.floor(min), Math.ceil(max)]);
              dispatch({ type: 'clear' as any, payload: undefined as any });
              handleClickOutside();
            }}
          >
            {getFilter()}
          </FilterLayout>
        );
      }
    }
    return component;
  };

  /**
   * dispatches into reducer and sets up the listener.
   */
  const handleClickOutside = () => {
    dispatch({ payload: false, type: 'showFilter' });
    handleListener(false);
  };

  /**
   * Detects outside click than component.
   * @param {HTMLElement} e
   */
  const handleClickEvent = (e: any) => {
    const path = e.path || (e.composedPath && e.composedPath());
    if (!path.includes(slideFilterRef.current)) {
      handleClickOutside();
    }
  };

  /**
   * starts or removes the event listener for mouse clicks.
   * @param {Boolean} bool : Start or remove the listener.
   */
  const handleListener = (bool: boolean) => {
    if (bool) {
      document.addEventListener('mousedown', handleClickEvent);
    } else {
      document.removeEventListener('mousedown', handleClickEvent);
    }
  };

  return (
    <div ref={slideFilterRef} className={styles.container}>
      {moreFilter && <Text type="subtitle" text={filter?.name} />}
      {!moreFilter && (
        <Button
          text={getButtonName()}
          height="25px"
          type={`chips${selectedValues?.length ? '' : '-secondary'}`}
          onClick={() => {
            dispatch({ payload: !showFilter, type: 'showFilter' });
            handleListener(!showFilter);
          }}
        />
      )}
      {getFilterComponent()}
    </div>
  );
}

export default SlideFilter;
